import { useParams } from 'react-router-dom';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { FC, Fragment, useState } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DealThesisFormModel } from '@app/modules/projects/inputs/deal-thesis-2/DealThesisNavigation';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  Pwerm2InputYearCalculationResultsDto,
  Pwerm2InputYearDebtInstrumentCalculationResultsPeriodByEventDto,
  Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto,
} from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';
import SvgAnalysis from '@app/shared/icons/Analysis';
import styles from './deal-thesis-sln-and-pref-share-section.module.scss';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { CorkscrewModal } from './CorkscrewModal';
import { translationKeys } from 'src/locale/setupI18n';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import { compareDeep } from '@app/shared/helpers/clone/compare-deep';
import { DealThesisSpacerRow2 } from '../DealThesisSpacerRow2';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import {
  FIELD_NAME_CASH_PAY_INTEREST,
  FIELD_NAME_CASH_PAY_PROPORTION,
  FIELD_NAME_COUPON_PERCENTAGE,
  FIELD_NAME_COUPON_TYPE,
  FIELD_NAME_OPENING_BALANCE,
  FIELD_NAME_CLOSING_BALANCE,
  FIELD_NAME_ADDITIONS,
  FIELD_NAME_ACCRUED_INTEREST,
  isBoldRow,
  resolveDisplayValues,
  getCellValueProps,
} from './utils/slnDisplayValues';
import { ViewChangeType } from '@app/shared/models/contracts/enums/shared-enums';
import { useSlnAndPrefShareData } from './hooks/useSlnAndPrefShareData';
import { HistoricEventData } from '../../deal-thesis-utils/useDealThesisData';

export const DealThesisCaseSlnAndPrefShareSectionERF: FC = () => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const project = useAppSelector((state) => state.project.projectDraft);

  const {
    caseResults,
    caseForecasts,
    filteredHistoricEventData,
    showHistoricDataColumns,
    getMaxColspan,
    instrumentPresentInData,
    shouldDisplayRow,
  } = useSlnAndPrefShareData(caseId!);

  const [isCorkscrewModalOpen, setIsCorkscrewModalOpen] = useState(false);
  const [selectedModalInstrumentId, setModalInstrumentId] = useState('');
  const [instrumentNarrative, setInstrumentNarrative] = useState('');
  const [eventData, setEventData] = useState<
    Pwerm2InputYearDebtInstrumentCalculationResultsPeriodByEventDto[]
  >([]);
  const [shouldHidePrevious, setShouldHidePrevious] = useState(true);
  const [shouldHideNext, setShouldHideNext] = useState(true);

  const { control } = useFormContext<DealThesisFormModel>();
  const { fields } = useFieldArray({
    name: 'slnAndPrefSharesInstruments',
    control,
  });

  const sortedFields = useInstrumentSort(fields);

  const determineCorkscrewNavigationButtonVisibility = (instrumentId: string, date: string) => {
    const allDates = [];

    const historicDates = Object.keys(caseResults.historicDates.historicEventYears ?? {});

    if (showHistoricDataColumns) {
      allDates.push(...historicDates);
    }

    allDates.push(project.valuationDate);

    const forecastDates = Object.keys(caseResults.forecastDates);
    allDates.push(...forecastDates);

    const hasMultipleEvents = (date: string) => {
      if (forecastDates.includes(date)) {
        const forecastEvents =
          caseResults.forecastDates?.[date]?.shareholderDebtInstrumentsEvents?.[instrumentId]
            ?.periodByEvent;
        return forecastEvents ? forecastEvents.length > 1 : false;
      } else if (historicDates.includes(date)) {
        const historicEvents =
          caseResults.historicDates.historicEventYears?.[date]?.shareholderDebtInstrumentsEvents?.[
            instrumentId
          ]?.periodByEvent;
        return historicEvents ? historicEvents.length > 1 : false;
      } else {
        const valuationEvents =
          caseResults.valuationDate?.shareholderDebtInstrumentsEvents?.[instrumentId]
            ?.periodByEvent;
        return valuationEvents ? valuationEvents.length > 1 : false;
      }
    };

    const firstDateWithMultipleEvents = allDates.find((date) => hasMultipleEvents(date));
    const lastDateWithMultipleEvents = allDates
      .slice()
      .reverse()
      .find((date) => hasMultipleEvents(date));

    const isFirstDateWithMultipleEvents = date === firstDateWithMultipleEvents;
    const isLastDateWithMultipleEvents = date === lastDateWithMultipleEvents;

    return {
      shouldHidePrevious: isFirstDateWithMultipleEvents,
      shouldHideNext: isLastDateWithMultipleEvents,
    };
  };

  const handleCorkscrewButtonClick = (
    isOpen: boolean,
    instrumentId: string,
    instrumentNarrative: string,
    eventData: Pwerm2InputYearDebtInstrumentCalculationResultsPeriodByEventDto[],
    eventDate: string
  ) => {
    const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
      instrumentId,
      eventDate
    );
    setShouldHidePrevious(shouldHidePrevious);
    setShouldHideNext(shouldHideNext);
    setIsCorkscrewModalOpen(isOpen);
    setModalInstrumentId(instrumentId);
    setInstrumentNarrative(instrumentNarrative);
    setEventData(eventData);
  };

  const handleCorkscrewForecastChange = (
    instrumentId: string,
    changeType: keyof typeof ViewChangeType
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allDates: [string, any][] = [];
    const historicDates = Object.entries(caseResults.historicDates.historicEventYears ?? {});

    if (showHistoricDataColumns) {
      allDates.push(...historicDates);
    }

    allDates.push([project.valuationDate, caseResults.valuationDate]);

    const forecastDates = Object.entries(caseResults.forecastDates);
    allDates.push(...forecastDates);

    const currentEventDate = allDates.find(([_, values]) =>
      compareDeep(values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent, eventData)
    )?.[0];

    const currentEventIndex = allDates.findIndex(([date]) => date === currentEventDate);

    if (changeType === ViewChangeType.Next) {
      const nextEventIndex = allDates
        .slice(currentEventIndex + 1)
        .findIndex(
          ([, values]) =>
            values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent.length > 1
        );

      if (nextEventIndex !== -1) {
        const nextEventDate = allDates[currentEventIndex + 1 + nextEventIndex][0];
        const nextEventData =
          nextEventDate in caseResults.forecastDates
            ? caseResults.forecastDates[nextEventDate].shareholderDebtInstrumentsEvents[
                instrumentId
              ].periodByEvent
            : nextEventDate in (caseResults.historicDates.historicEventYears ?? {})
            ? caseResults.historicDates.historicEventYears?.[nextEventDate]
                ?.shareholderDebtInstrumentsEvents?.[instrumentId]?.periodByEvent ?? []
            : caseResults.valuationDate.shareholderDebtInstrumentsEvents[instrumentId]
                .periodByEvent;
        setEventData(nextEventData);
        const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
          instrumentId,
          nextEventDate
        );
        setShouldHidePrevious(shouldHidePrevious);
        setShouldHideNext(shouldHideNext);
      }
    } else {
      const previousEventIndex = allDates
        .slice(0, currentEventIndex)
        .reverse()
        .findIndex(
          ([, values]) =>
            values.shareholderDebtInstrumentsEvents[instrumentId].periodByEvent.length > 1
        );

      if (previousEventIndex !== -1) {
        const previousEventDate = allDates[currentEventIndex - 1 - previousEventIndex][0];
        const previousEventData =
          previousEventDate in caseResults.forecastDates
            ? caseResults.forecastDates[previousEventDate].shareholderDebtInstrumentsEvents[
                instrumentId
              ].periodByEvent
            : previousEventDate in (caseResults.historicDates.historicEventYears ?? {})
            ? caseResults.historicDates.historicEventYears?.[previousEventDate]
                ?.shareholderDebtInstrumentsEvents?.[instrumentId]?.periodByEvent ?? []
            : caseResults.valuationDate.shareholderDebtInstrumentsEvents[instrumentId]
                .periodByEvent;
        setEventData(previousEventData);
        const { shouldHidePrevious, shouldHideNext } = determineCorkscrewNavigationButtonVisibility(
          instrumentId,
          previousEventDate
        );
        setShouldHidePrevious(shouldHidePrevious);
        setShouldHideNext(shouldHideNext);
      }
    }
  };

  const createTableRow = (
    title: translationKeys,
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto,
    instrumentId: string
  ) => {
    if (!shouldDisplayRow(instrumentId, key)) {
      return <></>;
    }

    return (
      <tr>
        <th className="table-primary__cell--vertical-separator">
          {isBoldRow(key) ? <strong>{title}</strong> : title}
        </th>
        <DealThesisEmptyHistoricDataColumns isHistoricEventsSection />
        {showHistoricDataColumns &&
          filteredHistoricEventData.map((historicYear) =>
            getHistoricDataCell(key, historicYear, instrumentId)
          )}
        {getValuationDateSummaryCell(key, instrumentId)}
        {caseForecasts.map(([forecastDate, forecastValue]) =>
          getCaseForecastCell(key, forecastDate, forecastValue, instrumentId)
        )}
        <td />
      </tr>
    );
  };

  const getHistoricDataCell = (
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto,
    historicYear: HistoricEventData,
    instrumentId: string
  ) => {
    const { displayValue, longText, isNoCoupon } = resolveDisplayValues(
      key,
      historicYear.shareholderDebtInstrumentsEvents[instrumentId]?.periodTotal
    );

    return (
      <td key={historicYear.historicEventsYear}>
        <CellValue
          value={displayValue}
          titleValue={longText}
          {...getCellValueProps(key, isNoCoupon)}
        />
      </td>
    );
  };

  const getValuationDateSummaryCell = (
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto,
    instrumentId: string
  ) => {
    const { displayValue, longText, isNoCoupon } = resolveDisplayValues(
      key,
      caseResults.valuationDate.shareholderDebtInstrumentsEvents[instrumentId]?.periodTotal
    );

    return (
      <td>
        <CellValue
          value={displayValue}
          titleValue={longText}
          {...getCellValueProps(key, isNoCoupon)}
        />
      </td>
    );
  };

  const getCaseForecastCell = (
    key: keyof Pwerm2InputYearDebtInstrumentCalculationResultsPeriodTotalDto,
    forecastDate: string,
    forecastValue: Pwerm2InputYearCalculationResultsDto,
    instrumentId: string
  ) => {
    const periodTotal = forecastValue.shareholderDebtInstrumentsEvents[instrumentId]?.periodTotal;
    const { displayValue, longText, isNoCoupon } = resolveDisplayValues(key, periodTotal);

    return (
      <td key={forecastDate}>
        <CellValue
          value={displayValue}
          titleValue={longText}
          {...getCellValueProps(key, isNoCoupon)}
        />
      </td>
    );
  };

  return !fields.length ? (
    <></>
  ) : (
    <>
      <tbody>
        <tr className="table-primary__row--plain">
          <th className="table-primary__cell--header">{l('_ShareholderDebtInstruments')}</th>
          <td className="table-primary__cell--header" colSpan={getMaxColspan() - 1} />
        </tr>
        {sortedFields.map((instrument) => {
          if (!instrumentPresentInData(instrument.instrumentId)) {
            return <Fragment key={instrument.instrumentId} />;
          }
          return (
            <Fragment key={instrument.id}>
              <tr className="table-primary__row--header">
                <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                  {instrument.instrumentNarrative}
                </th>
                <DealThesisEmptyHistoricDataColumns
                  isHistoricEventsSection
                  isStartSeparator
                  isEndSeparator
                />
                {showHistoricDataColumns &&
                  filteredHistoricEventData.map((historicYear) => {
                    return (
                      <td
                        key={historicYear.historicEventsYear}
                        className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                        {historicYear.shareholderDebtInstrumentsEvents[instrument.instrumentId]
                          ?.periodByEvent.length > 1 && (
                          <Button
                            className={styles['corkscrew-modal-icon']}
                            startIcon={<SvgAnalysis />}
                            onClick={() =>
                              handleCorkscrewButtonClick(
                                true,
                                instrument.instrumentId,
                                instrument.instrumentNarrative,
                                historicYear.shareholderDebtInstrumentsEvents[
                                  instrument.instrumentId
                                ].periodByEvent,
                                historicYear.historicEventsYear
                              )
                            }
                            autoIconSize
                            appearance={ButtonAppearance.CLEAN}
                            size={ButtonSize.SMALL}
                          />
                        )}
                      </td>
                    );
                  })}
                <td className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                  {caseResults.valuationDate.shareholderDebtInstrumentsEvents[
                    instrument.instrumentId
                  ]?.periodByEvent.length > 1 && (
                    <Button
                      className={styles['corkscrew-modal-icon']}
                      startIcon={<SvgAnalysis />}
                      onClick={() =>
                        handleCorkscrewButtonClick(
                          true,
                          instrument.instrumentId,
                          instrument.instrumentNarrative,
                          caseResults.valuationDate.shareholderDebtInstrumentsEvents[
                            instrument.instrumentId
                          ]?.periodByEvent,
                          project.valuationDate
                        )
                      }
                      autoIconSize
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.SMALL}
                    />
                  )}
                </td>
                {caseForecasts.map(([forecastDate, forecastValues]) => {
                  return (
                    <td
                      key={forecastDate}
                      className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                      {forecastValues.shareholderDebtInstrumentsEvents[instrument.instrumentId]
                        ?.periodByEvent.length > 1 && (
                        <Button
                          className={styles['corkscrew-modal-icon']}
                          startIcon={<SvgAnalysis />}
                          onClick={() =>
                            handleCorkscrewButtonClick(
                              true,
                              instrument.instrumentId,
                              instrument.instrumentNarrative,
                              forecastValues.shareholderDebtInstrumentsEvents[
                                instrument.instrumentId
                              ].periodByEvent,
                              forecastDate
                            )
                          }
                          autoIconSize
                          appearance={ButtonAppearance.CLEAN}
                          size={ButtonSize.SMALL}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="table-primary__cell--header-secondary table-primary__cell--section-start-separator table-primary__cell--section-end-separator" />
              </tr>
              {createTableRow(
                l('_OpeningBalance'),
                FIELD_NAME_OPENING_BALANCE,
                instrument.instrumentId
              )}
              {createTableRow(
                l('_InfusionOrRedemptionNegative'),
                FIELD_NAME_ADDITIONS,
                instrument.instrumentId
              )}
              {createTableRow(
                l('_AccruedInterest'),
                FIELD_NAME_ACCRUED_INTEREST,
                instrument.instrumentId
              )}
              {createTableRow(
                l('_ClosingBalance'),
                FIELD_NAME_CLOSING_BALANCE,
                instrument.instrumentId
              )}
              {createTableRow(
                l('_CouponPc'),
                FIELD_NAME_COUPON_PERCENTAGE,
                instrument.instrumentId
              )}
              {createTableRow(l('_CouponType'), FIELD_NAME_COUPON_TYPE, instrument.instrumentId)}
              {createTableRow(
                l('_CashPayProportion'),
                FIELD_NAME_CASH_PAY_PROPORTION,
                instrument.instrumentId
              )}
              {createTableRow(
                l('_CashInterest'),
                FIELD_NAME_CASH_PAY_INTEREST,
                instrument.instrumentId
              )}
              <DealThesisSpacerRow2 />
            </Fragment>
          );
        })}
      </tbody>
      <CorkscrewModal
        isOpen={isCorkscrewModalOpen}
        setIsOpen={setIsCorkscrewModalOpen}
        instrumentId={selectedModalInstrumentId}
        instrumentNarrative={instrumentNarrative}
        eventData={eventData}
        shouldHidePrevious={shouldHidePrevious}
        shouldHideNext={shouldHideNext}
        handleCorkscrewForecastChange={handleCorkscrewForecastChange}
      />
    </>
  );
};
